import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { useOrders } from '../../lib/orders/use-orders.js';
import { formatCurrency, formatDate } from '../../lib/format.js';
import { ListSkeleton } from '@tailoring-crm/ui';

/**
 * What this person has actually had made — the history half of the customer hub.
 *
 * THE MOST RECENT FEW, NOT ALL OF THEM, and the order book is one tap away for the rest. A
 * customer with sixty orders would otherwise turn a pane into a scroll, and the question this
 * answers at a counter is "what have we done for her lately", which five rows answer.
 *
 * It reads the order book's own list endpoint with a `customerId`, so the rows here and the rows
 * in `/orders` come from one query with one definition of what an order is worth.
 */
const RECENT_ORDER_COUNT = 5;

export function CustomerOrders({ customerId }: { customerId: string }): ReactElement {
  const { t } = useTranslation();
  const orders = useOrders({ customerId, filter: 'all', pageSize: RECENT_ORDER_COUNT });
  const items = orders.data?.items ?? [];

  if (orders.isPending) return <ListSkeleton rows={2} />;

  if (items.length === 0) {
    return (
      <section className="flex flex-col gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <ShoppingBag aria-hidden="true" className="size-4" />
          {t('customers.detail.ordersHeading')}
        </h3>
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {t('customers.detail.noOrders')}
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">{t('customers.detail.ordersHeading')}</h3>
      <ul className="flex flex-col gap-2">
        {items.map((order) => (
          <li key={order.id}>
            <Link
              to={`/orders/${order.id}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-lg border border-border p-3 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex flex-col">
                <span className="text-sm font-medium">{order.orderNumber}</span>
                <span className="text-xs text-muted-foreground">
                  {t('customers.detail.orderBooked', { date: formatDate(order.bookedAt) })}
                </span>
              </span>
              <span className="text-sm font-medium tabular-nums">
                {formatCurrency(order.totalPaise)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {/* Plainly the order book. The list there takes no customer filter from the URL, so a
          `?customerId=` here would be a parameter nothing reads — a link that quietly does
          something other than what it says. */}
      {orders.data && orders.data.total > items.length ? (
        <Link to="/orders" className="text-sm underline">
          {t('customers.detail.allOrders', { count: orders.data.total })}
        </Link>
      ) : null}
    </section>
  );
}
